import $ from 'jquery';
import React from 'react';
import PropTypes from 'prop-types';
import { format as formatCurrency, findCurrency } from 'currency-formatter';
import Card from '../bootstrap/Card';
import { secondsToHuman } from '../../lib/view_helpers';
import { rideDuration } from '../../lib/booking_helpers';

import VehicleNicknameLicensePlateLink from './VehicleNicknameLicensePlateLink';
import {
  getBookableOfferedSolutions,
  getHopAddress,
  getHopColor,
  getHopTime,
  getTransportModeIcon
} from './OfferedSolution.helpers';

export default class RideDetails extends React.Component {
  constructor(props) {
    super(props);
    this.t = window.locales.BookingWizard.RideDetails;
  }

  componentDidMount() {
    $('[data-toggle="popover"]').popover(
      {
        placement : 'bottom',
        html : true,
        trigger: 'focus hover'
      }
    );
  }

  formattedPrice() {
    if (this.props.ride.fare.booking_price) {
      const { booking_price: bookingPrice, currency } = this.props.ride.fare;
      const currencyObject = findCurrency(currency);

      if (currencyObject) {
        const price = bookingPrice / 10 ** currencyObject.decimalDigits;
        return formatCurrency(price, { code: currency });
      }

      return `${bookingPrice} ${currency}`;
    }

    return this.t.freeRide;
  }

  driverDetails() {
    if (!this.props.ride.driver) {
      return this.t.unknownDriver;
    }

    return `${this.props.ride.driver.first_name} ${this.props.ride.driver.last_name}`;
  }

  renderStationInfo(station) {
    if ( !station ) {
      return '';
    }

    let dataContent = ""
    if (station.description) {
      dataContent += `<p>${station.description}</p>`
    }
    if (station.avatar) {
      dataContent += `<img alt='${this.t.stationInfo}' src='${station.avatar.versions.small.url}' width='100%'/>`
    }

    return (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <button type="button"
              className="btn"
              data-toggle="popover"
              data-html="true"
              data-title={station.location_name}
              data-content={dataContent} >
        <i className='fa fa-info' />
      </button>
    );
  }

  renderIconLabelGroup(icon, label, value, iconStyle = undefined) {
    /* eslint-disable jsx-a11y/label-has-associated-control */
    return (
      <div className="d-flex flex-grow-1 gap-4">
        {icon && (
          <div className="d-inline-flex flex-shrink-0 align-items-center justify-content-center">
            <i className={`fa fa-fw ${icon} mr-1`} style={iconStyle} />
          </div>
        )}
        <div className="d-flex w-100 flex-column gap-0.5">
          {label && <label>{label}</label>}
          <span className="flex-wrap text-large">{value}</span>
        </div>
      </div>
    );
    /* eslint-enable jsx-a11y/label-has-associated-control */
  }

  renderHopTimeBadge(time) {
    const className = "d-inline-flex h-6 w-auto flex-shrink-0 align-items-center gap-1 px-2 py-0.5 text-large font-medium tabular-nums bg-ioki-gray-2 text-ioki-gray-8";
    return <span role="status" className={className}>{time}</span>;
  }

  renderJourney() {
    const t = (key) => this.t[key];
    const offeredSolutions = getBookableOfferedSolutions(this.props.ride);
    const bookedSolution = this.props.ride.booked_solution;
    const solutionToRender = bookedSolution || offeredSolutions[0];
    if (!solutionToRender) {
      return null;
    }
    const { hops } = solutionToRender;

    const originHop = hops[0];
    const { fromAddress: originFromAddress } = getHopAddress(
      originHop.transport_mode,
      originHop.from,
      originHop.to
    );
    const { fromTime: originFromTime } = getHopTime(
      originHop.transport_mode,
      originHop.from,
      originHop.to,
      this.props.timezone
    );

    const destinationHop = hops[hops.length - 1];
    const { toAddress: destinationToAddress } = getHopAddress(
      destinationHop.transport_mode,
      destinationHop.from,
      destinationHop.to
    );
    const { toTime: destinationToTime } = getHopTime(
      destinationHop.transport_mode,
      destinationHop.from,
      destinationHop.to,
      this.props.timezone
    );

    /* eslint-disable camelcase */
    return (
      <div className="d-flex flex-column gap-2 text-sm">
        {/* Origin hop */}
        <div className="d-flex align-items-center justify-content-between">
          {this.renderIconLabelGroup('fa-dot-circle-o', t('origin'), originFromAddress)}
          {this.renderHopTimeBadge(originFromTime)}
        </div>

        {/* All other hops (walking, drt, public transport) */}
        {hops.map(({ transport_mode, details, from, to, vehicle }, index) => {
          const hopColor = getHopColor(transport_mode, details?.transport_type);

          // hops of type walk are only displayed via icon
          if (transport_mode === 'walk')
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="d-inline-flex align-items-center justify-content-center" style={{width: "1.6rem", margin: "1.5rem 0"}}>
                <i className="fa fa-fw fa-ellipsis-v" />
              </div>
            );

          const isDrtHop = transport_mode === 'drt';
          const isPtHop = transport_mode === 'public_transport';
          /* eslint-enable camelcase */
          const fromLabel = isDrtHop ? t('pickup') : t('start');
          const toLabel = isDrtHop ? t('dropoff') : t('exit');
          const iconLabel = isDrtHop ? (
            <VehicleNicknameLicensePlateLink vehicle={vehicle} showIcon={false} />
          ) :
            details?.name;

          const { fromTime, toTime } = getHopTime(transport_mode, from, to, this.props.timezone);
          let { fromAddress, toAddress } = getHopAddress(transport_mode, from, to);

          if (from.station) {
            fromAddress =
              <span>
                {fromAddress}
                {this.renderStationInfo(from.station)}
              </span>
          }

          if (to.station) {
            toAddress =
              <span>
                {toAddress}
                {this.renderStationInfo(to.station)}
              </span>
          }

          let publicTransportHopDetails = null;
          if (isPtHop) {
            publicTransportHopDetails = (
              <span>
                <span>{details?.direction}</span>
                {
                  from.scheduled_platform ? (
                    <span className="rounded-lg m-2 p-1 bg-ioki-gray-2 text-ioki-gray-8">
                      {t('platform')}
                      {' '}
                      {from.scheduled_platform}
                    </span>
                  ) :
                    null
                }
              </span>
            );
          }

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="d-flex flex-column">
              {/* Hop start */}
              <div className="d-flex align-items-center justify-content-between">
                {this.renderIconLabelGroup('fa-circle-o', fromLabel, fromAddress, { fill: hopColor })}
                {this.renderHopTimeBadge(fromTime)}
              </div>

              {/* Connecting line between start/end with transport mode icon */}
              <div className="d-flex gap-4">
                <div className="d-flex align-items-center justify-content-center" style={{width: "1.6rem", height: "4rem"}}>
                  <span className="d-block" style={{backgroundColor: hopColor, width: ".125rem", height: "4rem"}} />
                </div>
                <div className="d-flex align-items-center gap-2 px-1">
                  {getTransportModeIcon(transport_mode, details?.transport_type, 24)}
                  <p className="text-xs">{iconLabel}</p>
                  {isPtHop ? publicTransportHopDetails : null}
                </div>
              </div>

              {/* Hop end */}
              <div className="d-flex align-items-center justify-content-between">
                {this.renderIconLabelGroup('fa-circle-o', toLabel, toAddress, { fill: hopColor })}
                {this.renderHopTimeBadge(toTime)}
              </div>
            </div>
          );
        })}

        {/* Destination hop */}
        <div className="d-flex align-items-center justify-content-between">
          {this.renderIconLabelGroup('fa-map-marker', t('destination'), destinationToAddress)}
          {this.renderHopTimeBadge(destinationToTime)}
        </div>
      </div>
    );
  }

  render() {
    const bookableSolutions = getBookableOfferedSolutions(this.props.ride);
    if (bookableSolutions.length === 0 && !this.props.ride.booked_solution) {
      return null;
    }

    /* eslint-disable react/jsx-one-expression-per-line */
    return (
      <>
        <div className="row">
          <div className="col" id="journey">
            <Card title={this.t.journey}>{this.renderJourney()}</Card>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <Card title={this.t.rideDetails}>
              <dl className="row">
                <dt className="col-sm-3 text-right">{this.t.rideDuration}</dt>
                <dd className="col-sm-9">
                  {secondsToHuman(rideDuration(this.props.ride))} {this.t.minutes}
                </dd>
                <dt className="col-sm-3 text-right">{this.t.driver}</dt>
                <dd className="col-sm-9">{this.driverDetails()} </dd>
                <dt className="col-sm-3 text-right">{this.t.vehicle}</dt>
                <dd className="col-sm-9">
                  {this.props.ride.vehicle.nickname} {` (${this.props.ride.vehicle.license_plate})`}
                </dd>
                <dt className="col-sm-3 text-right">{this.t.price}</dt>
                <dd className="col-sm-9">{this.formattedPrice()}</dd>
              </dl>
            </Card>
          </div>
        </div>
      </>
    );
  }
  /* eslint-enable react/jsx-one-expression-per-line */
}

RideDetails.propTypes = {
  ride: PropTypes.object.isRequired,
  timezone: PropTypes.string.isRequired
};
